import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Divider,
  Flex,
  Heading,
  Input,
  Spacer,
  Stack,
  Text
} from '@chakra-ui/react';

import { SFPlusCircleIcon } from '@assets/icons';
import { useSetDocumentTitle, useDebouncedProp } from '@util/hooks';
import { useInfiniteConversations } from '@services/query-services/conversations.query-service';
import { parseCommaSeparatedTags } from '@util/helpers';
import ConversationCard from '@components/settings/conversations-history/conversation-card.component';
import FeedScrollContainer from '@components/feed/feed-scroll-container.component';

export default function ConversationsHistoryPage() {
  useSetDocumentTitle('Conversation History - FORA');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery] = useDebouncedProp(searchQuery);

  const infiniteConversationsResult = useInfiniteConversations({
    sort: 'updated_at',
    asc: false,
    pageSize: 20,
    has_archived_at: false,
    query: debouncedSearchQuery ? `${debouncedSearchQuery}*` : undefined,
    tags: parseCommaSeparatedTags(searchParams.get('tags')),
    initiator_data_id: searchParams.get('initiator_data_id')
  });

  return (
    <Container
      maxW='container.md'
      px={{ base: 4, md: 0 }}
      py={11}
    >
      <Stack gap={6}>
        {/* Header */}
        <Flex
          align={{ base: 'flex-start', sm: 'center' }}
          flexDir={{ base: 'column', sm: 'row' }}
          gap={{ base: 1, sm: 4 }}
        >
          <Heading size='md'>Chat History</Heading>
          <Spacer />
          <Button
            variant='reskinPrimary'
            onClick={() => navigate('/explorer')}
            rightIcon={<SFPlusCircleIcon boxSize='14px' />}
            size='sm'
            fontWeight={500}
          >
            New Chat
          </Button>
        </Flex>

        <Divider />

        <Input
          border='reskin'
          placeholder='Search chats...'
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          borderRadius='md'
          size='md'
        />
        <FeedScrollContainer
          infiniteQueryResult={infiniteConversationsResult}
          noResultsText={
            debouncedSearchQuery
              ? 'No conversations found matching your search.'
              : 'No conversations found.'
          }
          loadingSkeleton={
            <Box
              p={4}
              borderRadius='md'
              border='1px'
              borderColor='gray.200'
              bg='gray.50'
            >
              <Text color='gray.400'>Loading conversation...</Text>
            </Box>
          }
          gap={3}
        >
          {(page) =>
            (page?.data || []).map((conversation) => (
              <ConversationCard
                key={conversation.id}
                conversation={conversation}
                onArchive={infiniteConversationsResult.refetch}
              />
            ))
          }
        </FeedScrollContainer>
      </Stack>
    </Container>
  );
}
